import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../../../api/axios";
import toast from "react-hot-toast";
import {
  Plus,
  Trash2,
  Edit,
  Search,
  ChevronLeft,
  ChevronRight,
  Package,
  Star,
} from "lucide-react";

export default function ProductListPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState("");
  const pageSize = 20;

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await api.get("/product", {
        params: { page, size: pageSize },
      });
      const data = response.data;
      setProducts(
        (data.products || []).map((p) => ({
          ...p,
          mainImage: p.productImages?.find((pi) => pi.isMain),
        }))
      );
      setTotalItems(data.totalItems || 0);
    } catch {
      toast.error("Ürünler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [page]);

  const handleDelete = (id) => {
    if (!window.confirm("Bu ürünü silmek istediğinize emin misiniz?")) return;
    api
      .delete(`/product/${id}`)
      .then(() => {
        toast.success("Ürün silindi");
        fetchProducts();
      })
      .catch(() => toast.error("Silme işlemi başarısız"));
  };

  const totalPages = Math.ceil(totalItems / pageSize);

  const filteredProducts = search
    ? products.filter(
        (p) =>
          (p.nameTr || "").toLowerCase().includes(search.toLowerCase()) ||
          (p.nameEn || "").toLowerCase().includes(search.toLowerCase()) ||
          (p.sku || "").toLowerCase().includes(search.toLowerCase())
      )
    : products;

  const formatPrice = (price) => {
    if (!price && price !== 0) return "—";
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
    }).format(price);
  };

  if (loading)
    return (
      <div className="page-loading">
        <div className="loading-spinner" />
        <p>Yükleniyor...</p>
      </div>
    );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2>Ürünler</h2>
          <p className="page-subtitle">
            {totalItems} ürün listeleniyor
          </p>
        </div>
        <Link to="/add-product" className="btn btn-primary">
          <Plus size={18} /> Yeni Ürün
        </Link>
      </div>

      <div className="content-card no-padding">
        <div className="table-toolbar">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Ürün ara (ad, SKU)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Görsel</th>
                <th>Ürün Adı</th>
                <th>SKU</th>
                <th>Kategori</th>
                <th>Fiyat</th>
                <th>Stok</th>
                <th>Durum</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (
                <tr key={product.productId}>
                  <td>
                    {product.mainImage?.imagePath ? (
                      <img
                        src={`${process.env.REACT_APP_UPLOAD_URL}/${product.mainImage.imagePath}`}
                        alt=""
                        className="table-thumb"
                      />
                    ) : (
                      <div className="table-thumb-placeholder">
                        <Package size={18} />
                      </div>
                    )}
                  </td>
                  <td className="fw-medium">
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      {product.nameTr || product.nameEn}
                      {product.isFeatured && (
                        <Star
                          size={14}
                          fill="var(--warning)"
                          color="var(--warning)"
                          title="Öne Çıkan"
                        />
                      )}
                    </div>
                  </td>
                  <td>
                    <code style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                      {product.sku || "—"}
                    </code>
                  </td>
                  <td>{product.productCategory?.nameTr || "—"}</td>
                  <td>
                    <div>
                      {product.discountPrice ? (
                        <>
                          <span
                            style={{
                              textDecoration: "line-through",
                              color: "var(--text-muted)",
                              fontSize: 12,
                            }}
                          >
                            {formatPrice(product.price)}
                          </span>
                          <br />
                          <span style={{ color: "var(--success)", fontWeight: 600 }}>
                            {formatPrice(product.discountPrice)}
                          </span>
                        </>
                      ) : (
                        <span style={{ fontWeight: 500 }}>
                          {formatPrice(product.price)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span
                      className={`status-badge ${
                        product.stock > (product.minStock || 0)
                          ? "active"
                          : product.stock === 0
                            ? "inactive"
                            : "warning"
                      }`}
                    >
                      {product.stock ?? "—"}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`status-badge ${product.isActive ? "active" : "inactive"}`}
                    >
                      {product.isActive ? "Aktif" : "Pasif"}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Link
                        to={`/edit-product/${product.productId}`}
                        className="btn-icon edit"
                        title="Düzenle"
                      >
                        <Edit size={16} />
                      </Link>
                      <button
                        onClick={() => handleDelete(product.productId)}
                        className="btn-icon delete"
                        title="Sil"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredProducts.length === 0 && (
          <div className="empty-state">
            <p>{search ? "Arama sonucu bulunamadı" : "Henüz ürün eklenmemiş"}</p>
            {!search && (
              <Link to="/add-product" className="btn btn-primary">
                <Plus size={18} /> İlk Ürünü Ekle
              </Link>
            )}
          </div>
        )}

        {totalPages > 1 && (
          <div className="table-pagination">
            <button
              className="btn btn-ghost btn-sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft size={16} /> Önceki
            </button>
            <span className="pagination-info">
              Sayfa {page} / {totalPages}
            </span>
            <button
              className="btn btn-ghost btn-sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Sonraki <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
